import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { App } from '@/App'
import { AuthProvider } from '@/contexts/AuthContext'
import { RealtimeProvider } from '@/contexts/RealtimeContext'
import { amplifyOutputs, IS_CONFIGURED } from '@/amplify-config'
import { queryClient } from '@/services/query-client'
import { Toaster } from '@/components/ui/Toaster'
import { ConfirmProvider } from '@/components/ui/ConfirmDialog'
import { applyEnvTheme } from '@/lib/app-env'
import '@/styles.css'

// Before anything paints: on non-prod builds this stamps <html data-env="…">,
// which swaps the penser palette to amber (see styles.css). No-op on main.
applyEnvTheme()

let reactMounted = false

function showFatalError(err: unknown) {
  // Only treat this as fatal if React never mounted. Stray rejections from
  // SDK background calls (Amplify, PWA icon fetches, etc.) shouldn't blank
  // the page once the app is up.
  if (reactMounted) {
    console.error('[late error]', err)
    return
  }
  const msg = err instanceof Error ? `${err.message}\n\n${err.stack ?? ''}` : String(err)
  const root = document.getElementById('root') ?? document.body
  const pre = document.createElement('pre')
  pre.style.cssText =
    'margin:24px;padding:16px;font:13px/1.5 ui-monospace,monospace;color:#7f1d1d;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;white-space:pre-wrap;overflow:auto;'
  pre.textContent = `[bootstrap error] ${msg}`
  root.innerHTML = ''
  root.appendChild(pre)
  console.error('[bootstrap error]', err)
}

window.addEventListener('error', (e) => showFatalError(e.error ?? e.message))
window.addEventListener('unhandledrejection', (e) => showFatalError(e.reason))

/**
 * True when this document is running inside an iframe.
 *
 * MSAL's silent flows (ssoSilent for the SSO auto-connect, and
 * acquireTokenSilent's renewal fallback) navigate a hidden iframe to Microsoft
 * and let Entra redirect it back to our own /outlook/return. MSAL only reads the
 * auth code off that frame's URL and then removes the frame — it never needs the
 * page to run. Booting the whole SPA in there would configure Amplify auth a
 * second time, open a second realtime WebSocket, and race the parent window's
 * own handleRedirectPromise for the same response. So: don't.
 *
 * Cross-origin framing is already refused by X-Frame-Options: SAMEORIGIN and
 * CSP frame-ancestors 'self' (customHttp.yml), so the only frames that reach
 * this check are our own.
 */
function isFramed(): boolean {
  try { return window.top !== window.self } catch { return true }  // cross-origin access threw → framed
}

async function bootstrap() {
  if (isFramed()) return
  if (IS_CONFIGURED) {
    const { Amplify } = await import('aws-amplify')
    // Amplify v6 supports two config shapes — the Gen-2 outputs file (flat
    // `auth: { user_pool_id, … }`) and the legacy manual format
    // (`Auth: { Cognito: { userPoolId, loginWith.oauth, … } }`). It does NOT
    // merge them, so to attach our OAuth hosted-UI config we have to build
    // the manual shape entirely and ignore the auto-import shortcut.
    const here = window.location.origin
    const userPoolId = amplifyOutputs.auth?.user_pool_id ?? ''
    // Each pool's hosted UI lives on its own domain (created out-of-band; CDK
    // doesn't manage them), so map by the pool the linked outputs point at.
    // An UNMAPPED pool falls through to PROD's domain, and the site then sends
    // its own client id to a domain that doesn't know it — Cognito answers
    // /error?error=invalid_request with no further detail. That is exactly how
    // cade-test-branch's first SSO attempt died (2026-08-18); if an env's pool
    // is ever recreated, its new pool id must be re-mapped here.
    //
    // Microsoft SSO works on every mapped env: each domain's
    // /oauth2/idpresponse is whitelisted on the Entra app (Azure Portal,
    // manual) and each pool carries the Microsoft IdP + its env's idp-proxy.
    // The wiring recipe lives in docs/cade-test-env-wiring.md.
    const COGNITO_DOMAINS: Record<string, string> = {
      'us-west-2_QOD5s00Gt': 'penser-bd-testing.auth.us-west-2.amazoncognito.com',
      'us-west-2_QCpNFGbQf': 'penser-bd-cade-test.auth.us-west-2.amazoncognito.com',
    }
    const cognitoDomain =
      COGNITO_DOMAINS[userPoolId] ?? 'penser-bd-portal.auth.us-west-2.amazoncognito.com'
    const userPoolClientId = amplifyOutputs.auth?.user_pool_client_id ?? ''
    const identityPoolId = (amplifyOutputs.auth?.identity_pool_id as string | undefined) ?? undefined
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId,
          userPoolClientId,
          ...(identityPoolId ? { identityPoolId } : {}),
          loginWith: {
            email: true,
            oauth: {
              domain: cognitoDomain,
              scopes: ['openid', 'email', 'profile'],
              redirectSignIn: [`${here}/auth/callback`],
              redirectSignOut: [`${here}/login`],
              responseType: 'code',
              providers: ['COGNITO'],
            },
          },
        },
      },
    } as Parameters<typeof Amplify.configure>[0])
  } else {
    console.warn('[bootstrap] amplify_outputs.json is empty — running in mock-auth mode')
  }

  // Resolve /msal-config.json so the Outlook ConnectButton can flip to
  // enabled state before first render (instead of needing a manual reload
  // after the config arrives).
  try {
    const { preloadMsalConfig } = await import('@/lib/msal')
    await preloadMsalConfig()
  } catch { /* MSAL pkg might be deferred; safe to ignore */ }

  const container = document.getElementById('root')
  if (!container) throw new Error('Root element #root not found')

  createRoot(container).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <RealtimeProvider>
              <ConfirmProvider>
                <App />
              </ConfirmProvider>
            </RealtimeProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster />
      </QueryClientProvider>
    </React.StrictMode>,
  )
  // Defer the flag so the first React commit has time to happen before any
  // synchronous error from a child component bubbles up.
  setTimeout(() => { reactMounted = true }, 100)
}

void bootstrap().catch(showFatalError)
